/**
 * Сколько секунд человек смотрит на блок цен лендинга без цен Paddle.
 *
 * Один показ — `paywall_shown` и один из трёх исходов (плюс поздний успех после
 * таймаута). `paywall_shown` (info) — знаменатель воронки: блок показан, дошли
 * до него цены или нет; ушедший до цены иначе нигде не виден.
 * - `paywall_price_ready` (success, `value` = секунды от показа до цен);
 * - `paywall_price_timeout` (slow) — через 8 с цен нет и ошибки не было.
 *   Цены, пришедшие позже, всё равно уходят `paywall_price_ready` с реальным
 *   временем — иначе распределение обрезалось бы ровно на хвосте;
 * - `paddle_init_error` / `paddle_price_preview_error` (error, `errorKind`) —
 *   Paddle.js не поднялся или превью цен упало/вернуло пусто. Контракт тот же,
 *   что у кабинета (`usePaddlePricing.js` фронтенда). Шлётся на показ, а не на
 *   загрузку страницы: отказ, случившийся до прокрутки, уходит в момент показа,
 *   поэтому ошибки и успехи делят один знаменатель — «дошли до тарифов».
 *   Ошибка гасит таймаут: одна поломка — одно событие.
 *
 * Логика без React и без DOM, чтобы её можно было проверить фейковыми часами
 * (`node --test`); хук `usePaywallPriceWaitObservability` только решает, когда
 * блок впервые попал на экран, и передаёт состояние цен.
 */
export const PAYWALL_PRICE_TIMEOUT_MS = 8_000;

export type PaywallWaitEvent =
  | { eventType: "paywall_shown"; outcome: "info"; endpointKind: "payment" }
  | { eventType: "paywall_price_ready"; outcome: "success"; value: number; endpointKind: "payment" }
  | { eventType: "paywall_price_timeout"; outcome: "slow"; endpointKind: "payment" }
  | { eventType: PaywallPriceFailureType; outcome: "error"; errorKind: string; endpointKind: "payment" };

export type PaywallPriceFailureType = "paddle_init_error" | "paddle_price_preview_error";

export interface PaywallPriceFailure {
  eventType: PaywallPriceFailureType;
  errorKind: string;
}

export interface PaywallWaitClock {
  now(): number;
  setTimeout(callback: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

const defaultClock: PaywallWaitClock = {
  now: () => (typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now()),
  setTimeout: (callback, ms) => setTimeout(callback, ms),
  clearTimeout: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

export interface PaywallPriceState {
  pricesReady: boolean;
  failure: PaywallPriceFailure | null;
}

export function createPaywallPriceWaitTracker(
  send: (event: PaywallWaitEvent) => void,
  clock: PaywallWaitClock = defaultClock,
) {
  let startedAt: number | null = null;
  let ready = false;
  let failed = false;
  let timedOut = false;
  let timer: unknown = null;

  function clearTimer() {
    if (timer === null) return;
    clock.clearTimeout(timer);
    timer = null;
  }

  return {
    /** Блок впервые на экране. Повторный вызов — тот же показ, t0 не сдвигается. */
    show() {
      if (startedAt !== null) return;
      startedAt = clock.now();
      send({ eventType: "paywall_shown", outcome: "info", endpointKind: "payment" });
    },

    /** Текущее состояние цен. Вызывать после `show()` и на каждое изменение. */
    sync({ pricesReady, failure }: PaywallPriceState) {
      if (startedAt === null) return;
      if (failure && !ready && !failed) {
        failed = true;
        clearTimer();
        send({
          eventType: failure.eventType,
          outcome: "error",
          errorKind: failure.errorKind,
          endpointKind: "payment",
        });
      }

      if (pricesReady && !ready) {
        ready = true;
        clearTimer();
        send({
          eventType: "paywall_price_ready",
          outcome: "success",
          value: Math.max(0, Math.round(clock.now() - startedAt)) / 1000,
          endpointKind: "payment",
        });
      }

      // Ошибка уже посчитана своим событием; «не дождались» поверх неё было бы
      // вторым счётом той же поломки.
      if (ready || failed || timedOut) {
        clearTimer();
        return;
      }
      if (timer !== null) return;

      const remaining = Math.max(0, PAYWALL_PRICE_TIMEOUT_MS - (clock.now() - startedAt));
      timer = clock.setTimeout(() => {
        timer = null;
        if (ready || failed || timedOut) return;
        timedOut = true;
        send({ eventType: "paywall_price_timeout", outcome: "slow", endpointKind: "payment" });
      }, remaining);
    },

    /**
     * Снять таймер (размонтирование, cleanup эффекта в StrictMode). Состояние
     * показа остаётся: повторный `sync()` взведёт таймер на остаток срока и не
     * пошлёт уже отправленное событие второй раз.
     */
    pause() {
      clearTimer();
    },
  };
}

export type PaywallPriceWaitTracker = ReturnType<typeof createPaywallPriceWaitTracker>;
